import React from "react";
import { MOCK } from "./data.jsx";
import { Ico, Spark, IASMeter, StatusPill } from "./primitives.jsx";
import { useResource, navigate } from "./api.jsx";

function _relativeTime(d) {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
function _formatAction(a) {
  return ({
    move: "Moved pipeline stage",
    score: "Re-scored",
    flag: "Flagged for review",
    note: "Note added",
    thesis_adopt: "Thesis adopted",
    assign: "Reassigned",
  })[a] || (a ? `${a[0].toUpperCase()}${a.slice(1)}` : "Activity");
}

export function Dashboard({ variant = "desk" }) {
  if (variant === "overview") return <DashboardOverview/>;
  return <DashboardDesk/>;
}

// Today rail: 3 curated decks with a specific reason to act NOW.
// This is the dashboard's highest-value surface — it's the difference between
// "here are your top decks by score" (generic) and "here is why this one matters today."
export const TODAY_RAIL = [
  {
    id: "helix",
    company: "Helix Therapeutics",
    tech: "mRNA oncology · Series A · $18M",
    ias: 82,
    sector: "Onc",
    urgency: "Founder call at 3:00 PM",
    urgencyType: "scheduled",
    why: "Mira Patel holds 30 min with you this afternoon. Thesis is drafted; IP and CMC are the two open questions to bring.",
    signals: [
      { label: "IP risk", tone: "warn", note: "single provisional, no composition-of-matter" },
      { label: "Preclinical", tone: "good", note: "3× tumor regression vs. published peers" },
    ],
    action: "Prep for meeting",
  },
  {
    id: "orbis",
    company: "Orbis Diagnostics",
    tech: "Cell-free DNA · Seed · $6M",
    ias: 74,
    sector: "Dx",
    urgency: "Competitor closed Series B yesterday",
    urgencyType: "market",
    why: "Grail spinout raised $45M at a $280M cap on Thursday — shifts the comparable set. Re-score recommended before you commit diligence hours.",
    signals: [
      { label: "Market", tone: "accent", note: "comp window narrowing" },
      { label: "Team", tone: "good", note: "2 ex-Grail, 1 ex-Foundation" },
    ],
    action: "Re-score now",
  },
  {
    id: "kiva",
    company: "Kiva Biosystems",
    tech: "Synbio enzymes · Series B · $34M",
    ias: 71,
    sector: "Syn",
    urgency: "Deck re-submitted with new IP",
    urgencyType: "update",
    why: "Jennifer Kim uploaded v3 on Sunday with the composition-of-matter patent granted March 28. Your prior concern on IP coverage is addressed.",
    signals: [
      { label: "IP", tone: "good", note: "CoM granted, 3 continuations" },
      { label: "Revenue", tone: "warn", note: "no signed LOIs yet" },
    ],
    action: "Review update",
  },
];

export function UrgencyIcon({ type }) {
  if (type === "scheduled") return <span style={{color:"var(--accent-hi)"}}>◔</span>;
  if (type === "market")    return <span style={{color:"var(--warn)"}}>◈</span>;
  if (type === "update")    return <span style={{color:"var(--good)"}}>↻</span>;
  return <span>·</span>;
}

export function TodayCard({ item, onOpen }) {
  const handleOpen = () => onOpen && onOpen(item.id);
  return (
    <div className="card today-card" onClick={handleOpen} style={{cursor:"pointer"}}>
      <div className="row" style={{gap:10, marginBottom:14, alignItems:"center"}}>
        <div style={{
          width:28, height:28, borderRadius:4,
          background:"var(--surface-2)", border:"1px solid var(--line)",
          display:"grid", placeItems:"center",
          fontFamily:"var(--ff-mono)", fontSize:10, color:"var(--text-mute)"
        }}>{item.sector}</div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontWeight:500, fontSize:15, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{item.company}</div>
          <div className="dim" style={{fontSize:12, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>{item.tech}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <div className="num" style={{fontSize:22, fontWeight:500, lineHeight:1}}>{item.ias}</div>
          <div className="h-eyebrow" style={{fontSize:9, marginTop:4, letterSpacing:".14em"}}>IAS</div>
        </div>
      </div>

      <div className="row" style={{gap:8, marginBottom:10, alignItems:"flex-start"}}>
        <div style={{marginTop:2, fontSize:14}}><UrgencyIcon type={item.urgencyType}/></div>
        <div style={{flex:1}}>
          <div style={{fontSize:13, fontWeight:500, color:"var(--text)", lineHeight:1.4}}>{item.urgency}</div>
        </div>
      </div>

      <div className="dim" style={{fontSize:13, lineHeight:1.55, marginBottom:14, textWrap:"pretty"}}>{item.why}</div>

      <div style={{display:"flex", flexDirection:"column", gap:6, marginBottom:16, borderTop:"1px solid var(--line)", paddingTop:12}}>
        {item.signals.map((s, i) => (
          <div key={i} className="row" style={{gap:8, fontSize:12}}>
            <span className={"pill pill--" + s.tone} style={{fontSize:10, minWidth:68, justifyContent:"center"}}>{s.label}</span>
            <span className="dim" style={{flex:1}}>{s.note}</span>
          </div>
        ))}
      </div>

      <button className="btn btn--ghost" style={{width:"100%", justifyContent:"center"}} onClick={(e) => { e.stopPropagation(); handleOpen(); }}>{item.action} →</button>
    </div>
  );
}

export function KPI({ label, value, delta, spark, good }) {
  return (
    <div className="card" style={{padding:"18px 20px"}}>
      <div className="h-eyebrow">{label}</div>
      <div style={{display:"flex", alignItems:"baseline", gap:10, marginTop:6}}>
        <div className="big-num">{value}</div>
        {delta && <span className={"pill " + (good ? "pill--good" : "pill--warn")} style={{fontSize:11}}>{delta}</span>}
      </div>
      {spark && <div style={{marginTop:10, height:40}}><Spark data={spark}/></div>}
    </div>
  );
}

export function DashboardDesk() {
  const me = useResource("/me/");
  const dash = useResource("/dashboard/");

  const user = me.data || MOCK.user;
  const kpis = dash.data?.kpis || {};
  const triage = dash.data?.triage || [];
  const todayRail = dash.data?.today_rail || [];
  const activity = dash.data?.activity || [];
  const top = triage.slice(0, 5);
  const displayName = user.name || user.email || "";
  const firstName = displayName.split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const goToResults = (id) => {
    // Only treat the arg as an id if it's a number or string; React MouseEvents
    // come through when the card wrapper onClick fires. In that case we still
    // navigate, but without a bogus analysis id.
    const extra = (typeof id === "number" || typeof id === "string") ? { analysisId: id } : {};
    navigate("results", extra);
  };

  // Loading / error states
  if (dash.loading) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{marginBottom:10}}>Loading…</div>
          <h1 className="page-title" style={{opacity:.5}}>Preparing your desk</h1>
        </div>
      </div>
    );
  }
  if (dash.error) {
    return (
      <div className="page route-enter">
        <div className="page-header">
          <div className="h-eyebrow" style={{marginBottom:10, color:"var(--bad)"}}>Error</div>
          <h1 className="page-title">Couldn't load the dashboard.</h1>
          <p className="page-sub" style={{marginTop:12}}>{String(dash.error.message || "Request failed")} (HTTP {dash.error.status})</p>
          <button className="btn btn--primary" style={{marginTop:16}} onClick={dash.reload}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page route-enter">
      {/* Hero row: greeting + pulse */}
      <div className="page-header" style={{alignItems:"flex-end"}}>
        <div>
          <div className="h-eyebrow" style={{marginBottom:10}}>{greet}, {user.name.split(" ")[0]} · {new Date().toLocaleDateString("en-US", {weekday:"long", month:"long", day:"numeric"})}</div>
          <h1 className="page-title">Nine decks in queue. <em>Three</em> worth your afternoon.</h1>
        </div>
        <div className="row" style={{gap:10}}>
          <button className="btn btn--ghost" onClick={() => navigate("pipeline")}><Ico n="folder" size={14}/> Pipeline</button>
          <button className="btn btn--primary" onClick={() => navigate("analyzer")}><Ico n="upload" size={14}/> New analysis</button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid--4" style={{marginBottom:32}}>
        <KPI label="Decks screened · 30d" value={kpis.decks_screened_30d ?? 0} good spark={[20,28,24,32,30,38,36,42,46,52,48,60]}/>
        <KPI label="Avg IAS" value={kpis.avg_ias ?? "—"} good spark={[62,61,64,63,66,65,68,67,69,68,70,68]}/>
        <KPI label="In diligence" value={kpis.in_diligence ?? 0} good spark={[2,3,3,4,4,5,5,6,6,7,7,7]}/>
        <KPI label="Pipeline value" value={kpis.pipeline_value ?? "—"} good spark={[280,290,310,305,330,340,360,370,380,395,405,412]}/>
      </div>

      {/* Today rail — the hero surface. Three cards, each with a specific reason to act now. */}
      <div className="row" style={{marginBottom:16, alignItems:"baseline"}}>
        <div>
          <div className="h-eyebrow">Today · {new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}</div>
          <div style={{fontFamily:"var(--ff-h)", fontSize:26, letterSpacing:"-0.01em", marginTop:6}}>
            Your <em style={{color:"var(--accent-hi)", fontStyle:"italic"}}>three</em> decisions.
          </div>
        </div>
        <div className="spacer"/>
        <span className="as-of">Curated 12 min ago · re-ranks hourly</span>
      </div>
      <div className="grid grid--3" style={{marginBottom:40, gap:20}}>
        {(todayRail.length
          ? todayRail.map(row => (
              <TodayCard
                key={row.id}
                item={{
                  id: row.id,
                  company: row.name,
                  tech: [row.tech, row.stage, row.inv].filter(Boolean).join(" · "),
                  ias: row.ias ?? 0,
                  sector: (row.sector || row.type || "VZ").slice(0, 3).toUpperCase(),
                  urgency: row.status === "diligence" ? "In diligence" : `Status · ${row.status}`,
                  urgencyType: row.status === "diligence" ? "scheduled" : "update",
                  why: "Scoped to your recent pipeline activity.",
                  signals: [],
                  action: "Open",
                }}
                onOpen={() => goToResults(row.id)}
              />
            ))
          : TODAY_RAIL.map(item => <TodayCard key={item.id} item={item} onOpen={goToResults}/>)
        )}
      </div>

      {/* Two-col: full triage table + side */}
      <div className="grid" style={{gridTemplateColumns:"1.6fr 1fr", gap:24, alignItems:"start"}}>
        {/* Full triage table (everything else) */}
        <div className="card card--flush">
          <div className="card-header">
            <div>
              <div className="eyebrow">Full queue</div>
              <div className="title">All decks awaiting a call</div>
            </div>
            <div className="row">
              <span className="pill"><i className="dot"/>Auto-ranked</span>
              <button className="btn btn--quiet" onClick={() => navigate("pipeline")}>View all →</button>
            </div>
          </div>
          <table className="t">
            <thead>
              <tr>
                <th style={{width:"38%"}}>Company</th>
                <th>Type</th>
                <th>Stage</th>
                <th style={{textAlign:"right"}}>IAS</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {top.length === 0 && (
                <tr>
                  <td colSpan={5} style={{textAlign:"center", padding:"40px 20px", color:"var(--text-mute)"}}>
                    No analyses in your scope yet. <button className="btn btn--primary" style={{marginLeft:12}} onClick={() => navigate("analyzer")}>Start your first</button>
                  </td>
                </tr>
              )}
              {top.map(c => {
                const sector = (c.sector || c.type || "VZ").slice(0, 3).toUpperCase();
                const iasVal = c.ias ?? 0;
                const iasDisplay = c.ias != null ? Math.round(c.ias) : "—";
                return (
                  <tr
                    key={c.id}
                    style={{cursor:"pointer"}}
                    onClick={() => goToResults(c.id)}
                  >
                    <td>
                      <div style={{display:"flex", alignItems:"center", gap:12}}>
                        <div style={{
                          width:32, height:32, borderRadius:4,
                          background:"var(--surface-2)",
                          border:"1px solid var(--line)",
                          display:"grid", placeItems:"center",
                          fontFamily:"var(--ff-mono)", fontSize:11, color:"var(--text-mute)"
                        }}>{sector}</div>
                        <div>
                          <div style={{fontWeight:500}}>{c.name}</div>
                          <div className="dim" style={{fontSize:12}}>{[c.tech, c.inv].filter(Boolean).join(" · ")}</div>
                        </div>
                      </div>
                    </td>
                    <td className="dim" style={{whiteSpace:"nowrap"}}>{c.type}</td>
                    <td className="dim" style={{whiteSpace:"nowrap"}}>{c.stage || "—"}</td>
                    <td style={{textAlign:"right"}}>
                      <div style={{display:"inline-flex", alignItems:"center", gap:8}}>
                        <div style={{width:60, height:4, background:"var(--line)", borderRadius:2, overflow:"hidden"}}>
                          <div style={{width:`${iasVal}%`, height:"100%", background:"var(--accent-hi)"}}/>
                        </div>
                        <span className="num" style={{fontWeight:500}}>{iasDisplay}</span>
                      </div>
                    </td>
                    <td><StatusPill status={c.status}/></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Side stack */}
        <div className="stack">
          <div className="card card--flush">
            <div className="card-header">
              <div>
                <div className="eyebrow">Pipeline mix</div>
                <div className="title">By modality · 30-day</div>
              </div>
            </div>
            <div className="card-body">
              {[
                { k: "Therapeutic", v: 42, pct: 38 },
                { k: "Diagnostic",  v: 31, pct: 28 },
                { k: "Device",      v: 24, pct: 22 },
                { k: "Digital Health", v: 13, pct: 12 },
              ].map(row => (
                <div key={row.k} style={{marginBottom:14}}>
                  <div style={{display:"flex", justifyContent:"space-between", fontSize:13, marginBottom:6}}>
                    <span>{row.k}</span>
                    <span className="num dim">{row.v} · {row.pct}%</span>
                  </div>
                  <div className="bar"><i style={{width:row.pct+"%"}}/></div>
                </div>
              ))}
            </div>
          </div>

          <div className="card card--flush">
            <div className="card-header">
              <div>
                <div className="eyebrow">Signal</div>
                <div className="title">Recent activity</div>
              </div>
              <span className="as-of">LIVE</span>
            </div>
            <div className="card-body" style={{padding:0}}>
              {activity.length === 0 && (
                <div style={{padding:"24px 20px", textAlign:"center", color:"var(--text-mute)", fontSize:13}}>
                  No recent activity.
                </div>
              )}
              {activity.map((a, i) => {
                const when = a.at ? new Date(a.at) : null;
                const rel = when ? _relativeTime(when) : (a.t || "");
                const text = a.what || a.note || _formatAction(a.action);
                const who = a.who || a.actor || "";
                return (
                  <div key={i} style={{
                    display:"grid", gridTemplateColumns:"80px 1fr",
                    gap:12, padding:"12px 20px",
                    borderBottom: i === activity.length-1 ? 0 : "1px solid var(--line)"
                  }}>
                    <div className="as-of" style={{paddingTop:2}}>{rel}</div>
                    <div>
                      <div style={{fontSize:13, lineHeight:1.5}}>{text}</div>
                      {who && <div className="dim" style={{fontSize:12, marginTop:2}}>{who}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Secondary block: modality strip */}
      <div className="rule" style={{margin:"48px 0 20px"}}>Portfolio snapshot</div>
      <div className="grid grid--3">
        <div className="card">
          <div className="h-eyebrow">Cycle time</div>
          <div className="big-num" style={{marginTop:6}}>11.4<span style={{fontSize:20, color:"var(--text-mute)", marginLeft:6}}>days</span></div>
          <div className="dim" style={{fontSize:13, marginTop:6}}>Median deck → decision</div>
          <Spark data={[14,13,13,12,12,11,12,11,11.5,11.4,11.4,11.4]}/>
        </div>
        <div className="card">
          <div className="h-eyebrow">Pass rate</div>
          <div className="big-num" style={{marginTop:6}}>12.7<span style={{fontSize:20, color:"var(--text-mute)", marginLeft:4}}>%</span></div>
          <div className="dim" style={{fontSize:13, marginTop:6}}>Of screened decks advance past first gate</div>
          <Spark data={[18,16,15,14,14,13,13,13.2,13,12.9,12.8,12.7]}/>
        </div>
        <div className="card">
          <div className="h-eyebrow">Model confidence</div>
          <div className="big-num" style={{marginTop:6}}>0.87</div>
          <div className="dim" style={{fontSize:13, marginTop:6}}>Avg extraction confidence across fields</div>
          <Spark data={[.78,.80,.81,.82,.83,.84,.85,.85,.86,.86,.87,.87].map(n=>n*100)}/>
        </div>
      </div>
    </div>
  );
}

export function DashboardOverview() {
  // A second layout — command-center feel, centered radar, big headline number
  const dash = useResource("/dashboard/");
  const kpis = dash.data?.kpis || {};
  const mix = dash.data?.pipeline_mix || {};
  const totalTracked = (mix.review || 0) + (mix.shortlist || 0) + (mix.diligence || 0) + (mix.pass || 0);
  const advancedCount = (mix.shortlist || 0) + (mix.diligence || 0);
  const advancementPct = totalTracked > 0 ? Math.round((advancedCount / totalTracked) * 1000) / 10 : 0;

  return (
    <div className="page route-enter">
      <div className="page-header" style={{borderBottom:0, paddingBottom:0, marginBottom:24}}>
        <div>
          <div className="h-eyebrow" style={{marginBottom:10}}>Fund overview · {new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})}</div>
          <h1 className="page-title" style={{fontSize:"var(--text-5xl)"}}>
            <em>{totalTracked}</em> decks<br/>under evaluation.
          </h1>
        </div>
      </div>

      <div className="grid" style={{gridTemplateColumns:"1fr 1fr 1fr", gap:24, alignItems:"start"}}>
        <div className="card" style={{textAlign:"center", padding:"30px 20px"}}>
          <div className="h-eyebrow" style={{marginBottom:12}}>Decks screened · 30d</div>
          <div style={{fontFamily:"var(--ff-h)", fontSize:88, lineHeight:.9, letterSpacing:"-0.03em", fontVariationSettings:'"opsz" 144'}}>{kpis.decks_screened_30d ?? 0}</div>
        </div>
        <div className="card" style={{textAlign:"center", padding:"30px 20px"}}>
          <div className="h-eyebrow" style={{marginBottom:12}}>Fund-wide IAS</div>
          <IASMeter value={kpis.avg_ias ?? 0} size={220} label="AVG"/>
        </div>
        <div className="card" style={{textAlign:"center", padding:"30px 20px"}}>
          <div className="h-eyebrow" style={{marginBottom:12}}>Advancement rate</div>
          <div style={{fontFamily:"var(--ff-h)", fontSize:88, lineHeight:.9, letterSpacing:"-0.03em", fontVariationSettings:'"opsz" 144'}}>{advancementPct}<span style={{fontSize:32, color:"var(--text-mute)"}}>%</span></div>
          <div className="dim" style={{marginTop:8, fontSize:13}}>Screen → shortlist/diligence</div>
        </div>
      </div>

      <div className="rule" style={{margin:"48px 0 24px"}}>By modality · 30-day flow</div>
      <div className="grid grid--4">
        {[
          ["Therapeutic", 42, "#4A8F7C"],
          ["Diagnostic", 31, "#62B39A"],
          ["Device", 24, "#D58A3B"],
          ["Digital Health", 13, "#BD5A4E"],
        ].map(([k,v,c])=>(
          <div key={k} className="card">
            <div style={{width:14, height:14, background:c, borderRadius:2, marginBottom:10}}/>
            <div style={{fontSize:"var(--text-md)"}}>{k}</div>
            <div className="big-num" style={{marginTop:4}}>{v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
